import { useEffect, useState } from "react"
import {
	IconPlusOutline18 as Plus,
	IconSquaresConnectedOutline18 as SquaresConnected,
	IconXmarkOutline18 as XIcon,
} from "nucleo-ui-outline-18"
import { useFieldArray } from "react-hook-form"
import { z } from "zod"

import type { SidebarSectionHeader } from "@productlane/zero"
import { providerUrlError } from "@productlane/lib/src/browser"
import { cn } from "@productlane/lib/src/cn"
import {
	Button,
	Form,
	FormInput,
	IconButton,
	SegmentedControl,
	useZodForm,
} from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { mutators } from "@productlane/zero/mutators"

import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { generateId } from "@/lib/id"
import { useZero } from "@/lib/zero"
import { SettingsContainer } from "../../settings-switches/settings-container"

export interface ConnectorFormData {
	id: string
	title: string
	url: string
	description: string | null
	type: "HTTP" | "MCP"
	headers: readonly SidebarSectionHeader[]
	oauthTokenUrl: string | null
	oauthClientId: string | null
	oauthScope: string | null
	oauthAudience: string | null
}

export interface ReusableHeader {
	headerId: string
	key: string
	sectionTitle: string
}

const AUTH_OPTIONS = [
	{ value: "headers", label: "Headers" },
	{ value: "oauth", label: "OAuth" },
] as const

const ConnectorFormSchema = z
	.object({
		title: z.string().trim().min(1, "Enter a name"),
		description: z.string().trim().max(1024).optional(),
		url: z
			.string()
			.trim()
			.min(1, "Enter the URL Productlane should request")
			.superRefine((value, ctx) => {
				const error = providerUrlError(value)
				if (error) ctx.addIssue({ code: "custom", message: error })
			}),
		headers: z.array(
			z
				.object({
					id: z.string().min(1),
					key: z.string().trim().min(1, "Enter a header name"),
					// Empty means "keep the stored value". Values never sync to the
					// client, so an existing header shows a masked placeholder instead.
					value: z.string().trim(),
					hasStoredValue: z.boolean(),
					copyOfHeaderId: z.string().optional(),
				})
				.superRefine((header, ctx) => {
					if (
						!header.value &&
						!header.hasStoredValue &&
						!header.copyOfHeaderId
					) {
						ctx.addIssue({
							code: "custom",
							path: ["value"],
							message: "Enter a header value",
						})
					}
				}),
		),
		authType: z.enum(["headers", "oauth"]),
		oauthTokenUrl: z.string().trim(),
		oauthClientId: z.string().trim(),
		oauthClientSecret: z.string().trim(),
		hasStoredSecret: z.boolean(),
		oauthScope: z.string().trim(),
		oauthAudience: z.string().trim(),
	})
	.superRefine((values, ctx) => {
		if (values.authType !== "oauth") return
		const urlError = values.oauthTokenUrl
			? providerUrlError(values.oauthTokenUrl)
			: "Enter the token URL"
		if (urlError) {
			ctx.addIssue({
				code: "custom",
				path: ["oauthTokenUrl"],
				message: urlError,
			})
		}
		if (!values.oauthClientId) {
			ctx.addIssue({
				code: "custom",
				path: ["oauthClientId"],
				message: "Enter the client ID",
			})
		}
		if (!values.oauthClientSecret && !values.hasStoredSecret) {
			ctx.addIssue({
				code: "custom",
				path: ["oauthClientSecret"],
				message: "Enter the client secret",
			})
		}
	})

type ConnectorFormValues = z.infer<typeof ConnectorFormSchema>

// The secret travels only when typed; otherwise the server uses the stored one.
function oauthPayload(values: ConnectorFormValues) {
	if (values.authType !== "oauth") return null
	return {
		tokenUrl: values.oauthTokenUrl,
		clientId: values.oauthClientId,
		...(values.oauthClientSecret && {
			clientSecret: values.oauthClientSecret,
		}),
		scope: values.oauthScope || null,
		audience: values.oauthAudience || null,
	}
}

interface MutationOutcome {
	type: string
	error?: { message?: string }
}

function saveFailed(message: string | undefined) {
	toast.error({
		title: message ?? "The connector could not be saved",
		icon: SquaresConnected,
	})
	return false
}

// A rejected mutator would otherwise look like a save that did nothing. It waits
// for the server too: the page that opens next reads the saved credentials there.
export async function saveConnector(mutation: {
	client: Promise<MutationOutcome>
	server: Promise<unknown>
}): Promise<boolean> {
	const outcome = await mutation.client
	if (outcome.type !== "success") {
		console.error("Connector save failed:", outcome)
		return saveFailed(outcome.error?.message)
	}
	try {
		const server = (await mutation.server) as MutationOutcome | undefined
		if (server && server.type !== "success") {
			return saveFailed(server.error?.message)
		}
	} catch (error) {
		return saveFailed(error instanceof Error ? error.message : undefined)
	}
	return true
}

export function ConnectorForm({
	type,
	data,
	nextOrder,
	reusableHeaders,
	onSaved,
}: {
	type: "HTTP" | "MCP"
	/** The connector being edited; null creates one. */
	data: ConnectorFormData | null
	nextOrder: number
	reusableHeaders: readonly ReusableHeader[]
	onSaved: (id: string) => void
}) {
	const zero = useZero()
	const isMcp = type === "MCP"

	const form = useZodForm({
		schema: ConnectorFormSchema,
		defaultValues: {
			title: data?.title ?? "",
			url: data?.url ?? "",
			description: data?.description ?? "",
			headers:
				data?.headers.map((header) => ({
					id: header.id,
					key: header.key,
					value: "",
					hasStoredValue: true,
				})) ?? [],
			authType: data?.oauthTokenUrl ? "oauth" : "headers",
			oauthTokenUrl: data?.oauthTokenUrl ?? "",
			oauthClientId: data?.oauthClientId ?? "",
			oauthClientSecret: "",
			hasStoredSecret: !!data?.oauthTokenUrl,
			oauthScope: data?.oauthScope ?? "",
			oauthAudience: data?.oauthAudience ?? "",
		},
	})
	const { fields, append, remove } = useFieldArray({
		name: "headers",
		control: form.control,
	})

	const testMutation = api.thread.testSidebarSection.useMutation()
	const mcpToolsMutation = api.customData.listMcpTools.useMutation()
	const [testResult, setTestResult] = useState<{
		success: boolean
		message: string
	} | null>(null)

	// A stale "everything looks good" is misleading, so any edit clears the result.
	useEffect(() => {
		const subscription = form.watch(() => setTestResult(null))
		return () => subscription.unsubscribe()
	}, [form])

	// A typed value travels as plaintext; an untouched one travels as the saved
	// header's id, which the server swaps for the stored value.
	const draftHeaderPayload = (values: ConnectorFormValues) =>
		values.authType === "oauth"
			? []
			: values.headers.map((h) =>
					h.value
						? { key: h.key, value: h.value }
						: { key: h.key, id: h.copyOfHeaderId ?? h.id },
				)

	const draftOAuthPayload = (values: ConnectorFormValues) => {
		const oauth = oauthPayload(values)
		return oauth ? { ...oauth, sectionId: data?.id } : null
	}

	const testHttp = async (values: ConnectorFormValues) => {
		try {
			const result = await testMutation.mutateAsync({
				url: values.url,
				headers: draftHeaderPayload(values),
				oauth: draftOAuthPayload(values),
			})
			if (!result.success) {
				setTestResult({ success: false, message: result.error })
				return
			}
			const actionLine = result.actionNames.length
				? `\nActions the agent can run: ${result.actionNames.join(", ")}.`
				: "\nNo actions declared yet."
			const agentLine = result.agentText
				? `\n\nWhat the agent reads:\n${result.agentText}`
				: ""
			setTestResult({
				success: true,
				message: `Everything looks good. The endpoint returned ${result.componentCount} component${result.componentCount === 1 ? "" : "s"}.${actionLine}${agentLine}`,
			})
		} catch {
			setTestResult({
				success: false,
				message: "Test request failed. Try again.",
			})
		}
	}

	const testMcp = async (values: ConnectorFormValues) => {
		try {
			const result = await mcpToolsMutation.mutateAsync({
				url: values.url,
				headers: draftHeaderPayload(values),
				oauth: draftOAuthPayload(values),
			})
			if (!result.success) {
				setTestResult({ success: false, message: result.error })
				return
			}
			const count = result.tools.length
			setTestResult({
				success: true,
				message: count
					? `Connected. ${count} ${count === 1 ? "tool" : "tools"} found.`
					: "Connected. This server exposes no tools yet.",
			})
		} catch {
			setTestResult({
				success: false,
				message: "Could not reach this MCP server. Check the URL and headers.",
			})
		}
	}

	const runTest = async () => {
		if (!(await form.trigger())) return
		analytics.track("sidebar-section-test")
		const values = form.getValues()
		await (isMcp ? testMcp(values) : testHttp(values))
	}

	const authType = form.watch("authType")
	const hasStoredSecret = form.watch("hasStoredSecret")
	const currentHeaders = form.watch("headers")
	const offeredHeaders = reusableHeaders.filter(
		(existing) =>
			!currentHeaders.some(
				(h) =>
					h.id === existing.headerId || h.copyOfHeaderId === existing.headerId,
			),
	)

	return (
		<Form
			form={form}
			onSubmit={async (values, e) => {
				if (e?.isPropagationStopped()) return
				const headers = (values.authType === "oauth" ? [] : values.headers).map(
					(h) => ({
						id: h.id,
						key: h.key,
						...(h.value ? { value: h.value } : {}),
						...(!h.value && h.copyOfHeaderId
							? { copyOfHeaderId: h.copyOfHeaderId }
							: {}),
					}),
				)

				if (data) {
					const saved = await saveConnector(
						zero.mutate(
							mutators.sidebarSections.update({
								id: data.id,
								title: values.title,
								url: values.url,
								description: values.description || null,
								type,
								headers,
								oauth: oauthPayload(values),
							}),
						),
					)
					if (!saved) return
					analytics.track("sidebar-section-update")
					toast.success({ title: "Connection saved", icon: SquaresConnected })
					onSaved(data.id)
					return
				}

				// A new MCP connector reaches the team's AI only; opening a tool to
				// customers stays an explicit pick. An HTTP one starts open, as before.
				const id = generateId()
				const created = await saveConnector(
					zero.mutate(
						mutators.sidebarSections.create({
							id,
							title: values.title,
							url: values.url,
							description: values.description || null,
							type,
							order: nextOrder,
							availableToAgent: !isMcp,
							allowedMcpTools: [],
							headers,
							oauth: oauthPayload(values),
						}),
					),
				)
				if (!created) return
				analytics.track("sidebar-section-create")
				onSaved(id)
			}}
		>
			<SettingsContainer className="space-y-4 divide-y-0">
				<FormInput name="title" label="Name" placeholder="Billing API" />
				<FormInput
					name="url"
					label={isMcp ? "Server URL" : "Endpoint URL"}
					placeholder={
						isMcp
							? "https://example.com/mcp"
							: "https://api.example.com/productlane"
					}
				/>
				<FormInput
					name="description"
					label="When the agent should use it"
					placeholder="Plans, seats and invoices for a customer's account."
					hint="Name the questions customers ask that this answers."
				/>

				<div>
					<div className="mb-1.5 flex items-center justify-between gap-3">
						<span className="text-sm font-medium">Sign-in</span>
						<SegmentedControl
							name="connector-auth"
							value={authType}
							options={AUTH_OPTIONS}
							onValueChange={(value) => form.setValue("authType", value)}
							showLabels
						/>
					</div>
					<p className="mb-2 text-xs tracking-tight text-secondary">
						{authType === "oauth"
							? "Productlane fetches a token with your client credentials and sends it as a bearer token."
							: "Sent with every request, for example an API key."}
					</p>
					{authType === "oauth" ? (
						<div className="space-y-2">
							<FormInput
								name="oauthTokenUrl"
								aria-label="Token URL"
								placeholder="Token URL"
							/>
							<div className="flex items-start gap-2">
								<div className="flex-1">
									<FormInput
										name="oauthClientId"
										aria-label="Client ID"
										placeholder="Client ID"
									/>
								</div>
								<div className="flex-1">
									<FormInput
										name="oauthClientSecret"
										type="password"
										autoComplete="off"
										aria-label="Client secret"
										placeholder={hasStoredSecret ? "••••••••" : "Client secret"}
									/>
								</div>
							</div>
							<div className="flex items-start gap-2">
								<div className="flex-1">
									<FormInput
										name="oauthScope"
										aria-label="Scope"
										placeholder="Scope (optional)"
									/>
								</div>
								<div className="flex-1">
									<FormInput
										name="oauthAudience"
										aria-label="Audience"
										placeholder="Audience (optional)"
									/>
								</div>
							</div>
						</div>
					) : (
						<div className="space-y-2">
							{fields.map((field, index) => (
								<div key={field.id} className="flex items-start gap-2">
									<div className="flex-1">
										<FormInput
											name={`headers.${index}.key`}
											aria-label="Header name"
											placeholder="Authorization"
										/>
									</div>
									<div className="flex-1">
										<FormInput
											name={`headers.${index}.value`}
											aria-label="Header value"
											placeholder={
												currentHeaders[index]?.hasStoredValue ||
												currentHeaders[index]?.copyOfHeaderId
													? "••••••••"
													: "Bearer ..."
											}
										/>
									</div>
									<div className="flex h-8 flex-none items-center">
										<IconButton
											Icon={XIcon}
											variant="ghost"
											onClick={() => remove(index)}
										/>
									</div>
								</div>
							))}
							<div className="flex items-center gap-2">
								<Button
									variant="outline"
									Icon={Plus}
									onClick={() =>
										append({
											id: generateId(),
											key: "",
											value: "",
											hasStoredValue: false,
										})
									}
								>
									Add header
								</Button>
								{offeredHeaders.length > 0 && (
									<DropdownMenu
										trigger={<Button variant="ghost">Reuse a header</Button>}
										contentProps={{ side: "bottom", align: "start" }}
									>
										<DropdownMenuGroup>
											{offeredHeaders.map((header) => (
												<DropdownMenuItem
													key={header.headerId}
													onClick={() =>
														append({
															id: generateId(),
															key: header.key,
															value: "",
															hasStoredValue: false,
															copyOfHeaderId: header.headerId,
														})
													}
												>
													<span className="flex min-w-0 items-baseline gap-1.5">
														<span className="font-medium">{header.key}</span>
														<span className="max-w-48 truncate text-xs text-tertiary">
															{header.sectionTitle}
														</span>
													</span>
												</DropdownMenuItem>
											))}
										</DropdownMenuGroup>
									</DropdownMenu>
								)}
							</div>
						</div>
					)}
				</div>

				{testResult ? (
					<div
						className={cn(
							"whitespace-pre-line text-xs",
							testResult.success
								? "text-green-600 dark:text-green-400"
								: "text-red-600 dark:text-red-400",
						)}
					>
						{testResult.message}
					</div>
				) : null}

				<div className="flex items-center justify-end gap-2">
					<Button
						variant="outline"
						onClick={() => void runTest()}
						isLoading={testMutation.isLoading || mcpToolsMutation.isLoading}
					>
						{isMcp ? "Test connection" : "Test request"}
					</Button>
					<Button type="submit">{data ? "Save" : "Connect"}</Button>
				</div>
			</SettingsContainer>
		</Form>
	)
}
